type GtmParams = {
	w: any,
	d: any,
	s: string,
	l: string,
	container_id: string
}

type VirtualPageParams = {
	path?: string,
	additionalData?: Record<string, any>
}

type EventParams = {
	eventName?: string,
	additionalPayload?: Record<string, any>,
}

type InitParams = {
	params?: Partial<GtmParams>,
	onload?: () => void,
	onerror?: (err: any) => void
}

class AnalyticsManager {
	loaded: boolean = false;
	error: any = null;
	
	gtmParams: GtmParams = {
		w: null,
		d: null,
		s: '',
		l: '',
		container_id: ''
	};

	init(
		{
			params = {},
			onload = () => {},
			onerror = () => {}
		}: InitParams = {}
	) {
		if (this.loaded) {
			console.warn("GTM already initialized, not initializing again");
			return;
		}

		this.gtmParams.w = params.w || window;
		this.gtmParams.d = params.d || document;
		this.gtmParams.s = params.s || 'script';
		this.gtmParams.l = params.l || 'dataLayer';
		this.gtmParams.container_id = params.container_id || this.getTagManagerContainer() || '';

		const { w, d, s, l, container_id } = this.gtmParams;
		
		if (!container_id) {
			const msg = "GTM Container ID not found! try initialize manually!";
			onerror(new Error(msg));
			console.error(msg);
			return;
		}

		w[l] = w[l] || [];
		w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
		const f = d.getElementsByTagName(s)[0];
		const j = d.createElement(s)
		const dl = l !== 'dataLayer' ? '&l=' + l : '';

		j.async = true;
		j.src = 'https://www.googletagmanager.com/gtm.js?id=' + container_id + dl;
		
		j.onload = () => {
			this.error = null;
			this.loaded = true;
			onload();
			console.info('GTM loaded');
		}

		j.onerror = (err: any) => {
			this.loaded = false;
			this.error = err;
			onerror(err);
			console.error('Error loading GTM script');
		}

		f.parentNode.insertBefore(j, f);
	}

	getTagManagerContainer(): string | null {
		const metaTag = document.querySelector('meta[name="gtm-container-id"]');
		if (metaTag) {
			const content = metaTag.getAttribute('content');
			if (content) {
				return content;
			}
		}

		return null;
	}

	pushToDataLayer(data: Record<string, any>) {
		const { w, l } = this.gtmParams;
		try {
			w[l].push(data);
		} catch (error) {
			console.error('Error pushing to data layer', error);
		}
	}

	pushEvent (eventParams: EventParams = {}){
		const event = eventParams.eventName;

		if (!event) {
			console.warn('Event name is required to push event to data layer');
			return;
		}

		this.pushToDataLayer({
			event,
			...eventParams.additionalPayload
		});
	}

	pushCustomEvent (eventParams: EventParams = {}) {
		const eventName = eventParams.eventName || 'unnamed_custom_event';
		const event = `CE ${eventName}`;
		this.pushToDataLayer({
			event,
			...eventParams.additionalPayload
		});
    }

	// dont need to call this when initial page is called, as gtm.js already pushes pageview
	pushVirtualPageView(virtualPageParams: VirtualPageParams = {}) {
		const { w } = this.gtmParams;
		const path = virtualPageParams.path || w.location.pathname;
		const additionalData = virtualPageParams.additionalData || {};
		let event = 'VP ' + path;

		this.pushToDataLayer({
			event,
			...additionalData
		});
	}

	autoPushVirtualPage() {
		const { w }= this.gtmParams;
		const pvp = this.pushVirtualPageView.bind(this);

		(function(history: History){
			const push = history.pushState;
			history.pushState = function (
				_: any, // "data" param
				__: string, // "unused" param
				url?: string | URL | null
			) {
				const ret = push.apply(this, arguments as any);
				if(url) pvp();
				w.dispatchEvent(new Event("urlchange"));
				return ret;
			};

			const replace = history.replaceState;
			history.replaceState = function (
				_: any, // "data" param
				__: string, // "unused" param
				url?: string | URL | null
			) {
				const ret = replace.apply(this, arguments as any);
				if(url) pvp();
				w.dispatchEvent(new Event("urlchange"));
				return ret;
			};
		})(w.history);
	}
}

// singleton
export default new AnalyticsManager();